import { Component, OnInit } from "@angular/core";
import { faCheck, faEraser, faPlus, faSave, faUndo } from "@fortawesome/free-solid-svg-icons";
import { NotifierService } from "angular-notifier";
import { FormField } from "src/app/interfaces/form-field.model";
import { DialogService } from "src/app/services/dialog.service";
import {
  ShipmentMaster,
  ShipmentMasterService,
} from "src/app/services/sales/shipment-master.service";

type ShipmentTab = "newShipment" | "updateShipment" | "shipmentHistory";

@Component({
  selector: "app-shipment-master",
  templateUrl: "./shipment-master.component.html",
  styleUrls: ["./shipment-master.component.css"],
})
export class ShipmentMasterComponent implements OnInit {
  faPlus = faPlus;
  faEraser = faEraser;
  faSave = faSave;
  faCheck = faCheck;
  faUndo = faUndo;

  selectedTab: ShipmentTab = "newShipment";
  isEditingShipment = false;
  shipmentDetails?: ShipmentMaster;

  modelOptions: any[] = [{ value: "", display: "Please select" }];
  newShipmentFormFields: FormField[] = [];
  shipmentEditFormFields: FormField[] = [];
  updateShipmentEntryFormFields: FormField[] = [this.shipmentEntryField("updateShipmentEntry")];
  shipmentHistoryFormFields: FormField[] = [this.shipmentEntryField("shipmentHistoryEntry")];
  updateShipmentHistoryFormFields: FormField[] = [
    { type: "text", id: "updateRemarks", label: "Update Remark(s)", value: "", maxLength: 100 },
    { type: "date", id: "updateEtaDate", label: "Update ETA Date", value: "" },
  ];

  pendingShipments: ShipmentMaster[] = [];
  newShipmentTableEntries: any[] = [];
  updateShipmentTableEntries: any[] = [];
  shipmentHistoryTableEntries: any[] = [];

  newShipmentTableHeaders = [
    { title: "ENTRY DATE", type: "data", id: "entryDate" },
    { title: "ARRIVAL DATE", type: "data", id: "arrivalDate" },
    { title: "MODEL", type: "data", id: "modelName" },
    { title: "MODEL CODE", type: "data", id: "modelCode" },
    { title: "OCC NUMBER", type: "data", id: "occNumber" },
    { title: "COLOUR CODE", type: "data", id: "extColourCode" },
    { title: "TYPE", type: "data", id: "extColourType" },
    { title: "INTERIOR CODE", type: "data", id: "intColourCode" },
    { title: "QUANTITY", type: "data", id: "quantity" },
    { title: "ACTION", type: "trash", id: "action" },
  ];

  updateShipmentTableHeaders = [
    { title: "NO", type: "data", id: "no" },
    ...this.shipmentTableDataHeaders(),
    { title: "ACTION", type: "editTrash", id: "action" },
  ];

  shipmentHistoryTableHeaders = [
    { title: "", type: "checkbox", id: "checked" },
    ...this.shipmentTableDataHeaders(),
    { title: "ETA REMARK", type: "data", id: "etaRemark" },
  ];

  constructor(
    private shipmentMasterService: ShipmentMasterService,
    private notifier: NotifierService,
    private dialogService: DialogService,
  ) {}

  ngOnInit(): void {
    this.resetToInitialState();
  }

  private resetToInitialState(): void {
    this.selectedTab = "newShipment";
    this.isEditingShipment = false;
    this.shipmentDetails = undefined;
    this.newShipmentFormFields = this.createShipmentFields();
    this.shipmentEditFormFields = this.createShipmentFields(true);
    this.updateShipmentEntryFormFields = [this.shipmentEntryField("updateShipmentEntry")];
    this.shipmentHistoryFormFields = [this.shipmentEntryField("shipmentHistoryEntry")];
    this.updateShipmentHistoryFormFields = [
      { type: "text", id: "updateRemarks", label: "Update Remark(s)", value: "", maxLength: 100 },
      { type: "date", id: "updateEtaDate", label: "Update ETA Date", value: "" },
    ];
    this.pendingShipments = [];
    this.newShipmentTableEntries = [];
    this.updateShipmentTableEntries = [];
    this.shipmentHistoryTableEntries = [];
    this.loadActiveModelList();
    this.refreshShipmentEntryDates();
  }

  onTabSelect(tab: ShipmentTab): void {
    this.selectedTab = tab;
    this.isEditingShipment = false;
  }

  onNewShipmentFormChange(event: any): void {
    const fieldId = this.newShipmentFormFields[event?.updatedValue]?.id;
    if (fieldId === "modelDescription") {
      this.onModelSelected(this.newShipmentFormFields);
    } else if (fieldId === "exteriorColourCode") {
      this.onExteriorColourSelected(this.newShipmentFormFields);
    }
  }

  onUpdateShipmentEntryChange(event: any): void {
    if (this.updateShipmentEntryFormFields[event?.updatedValue]?.id === "updateShipmentEntry") {
      this.loadShipmentsFor("updateShipment");
    }
  }

  onShipmentHistoryEntryChange(event: any): void {
    if (this.shipmentHistoryFormFields[event?.updatedValue]?.id === "shipmentHistoryEntry") {
      this.loadShipmentsFor("shipmentHistory");
    }
  }

  onEditShipmentFormChange(event: any): void {
    const fieldId = this.shipmentEditFormFields[event?.updatedValue]?.id;
    if (fieldId === "modelDescription") {
      this.onModelSelected(this.shipmentEditFormFields);
    } else if (fieldId === "exteriorColourCode") {
      this.onExteriorColourSelected(this.shipmentEditFormFields);
    }
  }

  onAddEntry(): void {
    const modelDescription = this.valueOf(this.newShipmentFormFields, "modelDescription");
    const modelOption = this.modelOptions.find((option) => option.value === modelDescription);
    const quantity = this.toNumber(this.valueOf(this.newShipmentFormFields, "quantity"));
    const entryDate = this.toBackendDate(this.valueOf(this.newShipmentFormFields, "shipmentDate"));
    const arrivalDate = this.toBackendDate(this.valueOf(this.newShipmentFormFields, "arrivalDate"));

    if (
      !entryDate ||
      !arrivalDate ||
      !modelOption ||
      !this.valueOf(this.newShipmentFormFields, "modelCode") ||
      !this.valueOf(this.newShipmentFormFields, "exteriorColourCode") ||
      !this.valueOf(this.newShipmentFormFields, "interiorColourCode") ||
      quantity === null
    ) {
      this.notifier.notify("error", "Please fill in all required fields.");
      return;
    }
    if (quantity < 0 || !Number.isInteger(quantity)) {
      this.notifier.notify("error", "Quantity must be a whole number greater than or equal to zero.");
      return;
    }
    if (this.isDateAfter(entryDate, arrivalDate)) {
      this.notifier.notify("error", "Estimated Date of Arrival cannot be earlier than Shipment Date of Entry.");
      return;
    }

    const shipment = this.buildShipmentFromFields(this.newShipmentFormFields, {
      ShipmentMasterOrderNo: 0,
      ModelGroup: modelDescription.split(" ")[0],
      Classification: modelOption.carType === "NC" ? "NORMAL" : "TAXI",
      ReceivedQuantity: 0,
      IntColourType: null,
      OrdersRemark: null,
      EtaRemark: null,
    });
    this.pendingShipments = [...this.pendingShipments, shipment];
    this.refreshPendingEntries();
    this.onClearForm();
  }

  onRemoveEntry(entry: any): void {
    this.pendingShipments = this.pendingShipments.filter((shipment) => shipment !== entry.shipment);
    this.refreshPendingEntries();
    this.notifier.notify("success", "Entry removed successfully.");
  }

  onClearForm(): void {
    this.newShipmentFormFields.forEach((field) => (field.value = ""));
  }

  onSaveEntries(): void {
    if (this.pendingShipments.length === 0) {
      return;
    }

    this.shipmentMasterService.addShipment(this.pendingShipments).subscribe({
      next: (response) => {
        if (!this.isSuccessful(response)) {
          this.notifier.notify("error", response.message || "Failed to save new shipment.");
          return;
        }
        this.pendingShipments = [];
        this.refreshPendingEntries();
        this.refreshShipmentEntryDates();
        this.notifier.notify("success", "New shipment saved successfully.");
      },
      error: () => this.notifier.notify("error", "Failed to save new shipment."),
    });
  }

  editShipmentEntry(entry: any): void {
    this.shipmentMasterService.getShipmentDetails(entry.no).subscribe({
      next: (response) => {
        if (!response.body) {
          this.notifier.notify("error", "No shipment details found for the selected shipment.");
          return;
        }
        this.shipmentDetails = response.body;
        this.isEditingShipment = true;
        this.setEditFormValues(response.body);
      },
      error: () => this.notifier.notify("error", "Failed to retrieve shipment details."),
    });
  }

  async removeShipmentEntry(entry: any): Promise<void> {
    const confirmed = await this.dialogService.openDeleteDialog(`shipment ${entry.no}`);
    if (!confirmed) {
      return;
    }

    this.shipmentMasterService.deleteShipment(entry.no).subscribe({
      next: (response) => {
        if (!this.isSuccessful(response)) {
          this.notifier.notify("error", response.message || "The shipment was not deleted.");
          return;
        }
        this.notifier.notify("success", "Shipment deleted successfully.");
        this.loadShipmentsFor("updateShipment");
        this.refreshShipmentEntryDates();
      },
      error: () => this.notifier.notify("error", "Failed to delete shipment."),
    });
  }

  onUpdateShipmentDetails(): void {
    if (!this.shipmentDetails) {
      return;
    }
    const quantity = this.toNumber(this.valueOf(this.shipmentEditFormFields, "quantity"));
    const entryDate = this.toBackendDate(this.valueOf(this.shipmentEditFormFields, "shipmentDate"));
    const arrivalDate = this.toBackendDate(this.valueOf(this.shipmentEditFormFields, "arrivalDate"));
    if (
      quantity === null ||
      quantity < 0 ||
      !Number.isInteger(quantity) ||
      !entryDate ||
      !arrivalDate ||
      !this.valueOf(this.shipmentEditFormFields, "modelCode") ||
      !this.valueOf(this.shipmentEditFormFields, "exteriorColourCode") ||
      !this.valueOf(this.shipmentEditFormFields, "interiorColourCode")
    ) {
      this.notifier.notify("error", "Please fill in all required fields.");
      return;
    }
    if (this.isDateAfter(entryDate, arrivalDate)) {
      this.notifier.notify("error", "Estimated Date of Arrival cannot be earlier than Shipment Date of Entry.");
      return;
    }
    if (
      this.shipmentDetails.ReceivedQuantity !== null &&
      this.shipmentDetails.ReceivedQuantity !== undefined &&
      quantity < this.shipmentDetails.ReceivedQuantity
    ) {
      this.notifier.notify("error", `Quantity cannot be below ${this.shipmentDetails.ReceivedQuantity}.`);
      return;
    }

    const selectedModel = this.modelOptions.find(
      (option) => option.value === this.valueOf(this.shipmentEditFormFields, "modelDescription"),
    );
    const model: ShipmentMaster = this.buildShipmentFromFields(this.shipmentEditFormFields, {
      ...this.shipmentDetails,
      ModelGroup: selectedModel?.modelGroup ?? this.shipmentDetails.ModelGroup,
      Classification: this.shipmentDetails.Classification,
      ReceivedQuantity: this.shipmentDetails.ReceivedQuantity,
      IntColourType: this.shipmentDetails.IntColourType,
      OrdersRemark: this.shipmentDetails.OrdersRemark,
      EtaRemark: this.shipmentDetails.EtaRemark,
    });
    if (!model.ModelName) {
      model.ModelName = this.shipmentDetails.ModelName;
    }

    this.shipmentMasterService.updateShipment(model).subscribe({
      next: (response) => {
        if (!this.isSuccessful(response)) {
          this.notifier.notify("error", response.message || "Failed to update shipment.");
          return;
        }
        this.notifier.notify("success", "Shipment updated successfully.");
        this.onCancelEdit();
        this.loadShipmentsFor("updateShipment");
      },
      error: () => this.notifier.notify("error", "Failed to update shipment."),
    });
  }

  onCancelEdit(): void {
    this.resetToInitialState();
  }

  setAllHistoryEntries(checked: boolean): void {
    this.shipmentHistoryTableEntries.forEach((entry) => (entry.checked = checked));
  }

  onUpdateEtaRemarks(): void {
    const selected = this.selectedHistoryOrderNumbers();
    const value = String(this.valueOf(this.updateShipmentHistoryFormFields, "updateRemarks") ?? "").trim();
    if (selected.length === 0) {
      this.notifier.notify("error", "Please select at least one shipment.");
      return;
    }
    if (!value) {
      this.notifier.notify("error", "Remarks cannot be empty.");
      return;
    }
    this.shipmentMasterService.updateRemarks({ OrderNoList: selected, Value: value }).subscribe({
      next: (response) => {
        if (!this.isSuccessful(response)) {
          this.notifier.notify("error", response.message || "Failed to update remarks.");
          return;
        }
        this.field(this.updateShipmentHistoryFormFields, "updateRemarks").value = "";
        this.notifier.notify("success", "Remarks updated successfully.");
        this.loadShipmentsFor("shipmentHistory");
      },
      error: () => this.notifier.notify("error", "Failed to update remarks."),
    });
  }

  onUpdateEtaDate(): void {
    const selected = this.selectedHistoryOrderNumbers();
    const value = this.toBackendDate(this.valueOf(this.updateShipmentHistoryFormFields, "updateEtaDate"));
    if (selected.length === 0) {
      this.notifier.notify("error", "Please select at least one shipment.");
      return;
    }
    if (!value) {
      this.notifier.notify("error", "Please select a valid ETA date.");
      return;
    }
    this.shipmentMasterService.updateArrivalDate({ OrderNoList: selected, Value: value }).subscribe({
      next: (response) => {
        if (!this.isSuccessful(response)) {
          this.notifier.notify("error", response.message || "Failed to update ETA date.");
          return;
        }
        this.field(this.updateShipmentHistoryFormFields, "updateEtaDate").value = "";
        this.notifier.notify("success", "ETA date updated successfully.");
        this.loadShipmentsFor("shipmentHistory");
      },
      error: () => this.notifier.notify("error", "Failed to update ETA date."),
    });
  }

  private loadActiveModelList(): void {
    this.shipmentMasterService.getBasicActiveModelList().subscribe({
      next: (response) => {
        if (!Array.isArray(response.body)) {
          this.notifier.notify("error", "Unable to load the active model list.");
          return;
        }
        this.modelOptions = [
          { value: "", display: "Please select" },
          ...response.body.map((model: any) => ({
            value: model.ModelShortDesc,
            display: model.ModelShortDesc,
            modelCode: model.ModelCode,
            modelGroup: model.ModelShort,
            occNumber: model.OccNo,
            carType: model.CarType,
          })),
        ];
        this.setModelOptions(this.newShipmentFormFields);
        this.setModelOptions(this.shipmentEditFormFields);
      },
      error: () => this.notifier.notify("error", "Unable to load the active model list."),
    });
  }

  private refreshShipmentEntryDates(): void {
    this.shipmentMasterService.getShipmentEntryDates().subscribe({
      next: (response) => {
        const options = [
          { value: "", display: "Please select" },
          ...(response.body ?? []).map((date) => ({ value: date, display: date })),
        ];
        this.field(this.updateShipmentEntryFormFields, "updateShipmentEntry").options = options;
        this.field(this.shipmentHistoryFormFields, "shipmentHistoryEntry").options = options;
      },
      error: () => this.notifier.notify("error", "Failed to fetch shipment entry dates."),
    });
  }

  private loadShipmentsFor(tab: "updateShipment" | "shipmentHistory"): void {
    const fields = tab === "updateShipment" ? this.updateShipmentEntryFormFields : this.shipmentHistoryFormFields;
    const fieldId = tab === "updateShipment" ? "updateShipmentEntry" : "shipmentHistoryEntry";
    const selectedDate = this.valueOf(fields, fieldId);
    if (!selectedDate) {
      return;
    }
    this.shipmentMasterService.getAllShipmentByDate(String(selectedDate).replace(/\//g, "-")).subscribe({
      next: (response) => {
        const entries = (response.body ?? []).map((shipment) => this.toTableEntry(shipment, tab === "shipmentHistory"));
        if (tab === "updateShipment") {
          this.updateShipmentTableEntries = entries;
        } else {
          this.shipmentHistoryTableEntries = entries;
        }
      },
      error: () => this.notifier.notify("error", "Failed to fetch shipments for the selected date."),
    });
  }

  private onModelSelected(fields: FormField[]): void {
    const selected = this.modelOptions.find((option) => option.value === this.valueOf(fields, "modelDescription"));
    if (!selected) {
      return;
    }
    this.field(fields, "modelCode").value = selected.modelCode ?? "";
    this.field(fields, "occNumber").value = selected.occNumber ?? "";
    this.field(fields, "exteriorColourCode").value = "";
    this.field(fields, "interiorColourCode").value = "";
    this.field(fields, "type").value = "";
    this.field(fields, "exteriorColourCode").options = [{ value: "", display: "Please select" }];
    this.field(fields, "interiorColourCode").options = [{ value: "", display: "Please select" }];
    this.loadExteriorColours(fields, selected.modelGroup, selected.value);
  }

  private onExteriorColourSelected(fields: FormField[]): void {
    const exterior = this.optionOf(fields, "exteriorColourCode");
    if (!exterior) {
      return;
    }
    this.field(fields, "type").value = exterior.type ?? "";
    this.field(fields, "interiorColourCode").value = "";
    this.field(fields, "interiorColourCode").options = [{ value: "", display: "Please select" }];
    this.loadInteriorColours(fields, this.valueOf(fields, "modelDescription"), exterior.value);
  }

  private loadExteriorColours(fields: FormField[], modelGroup: string, modelDescription: string, selectedCode?: string): void {
    this.shipmentMasterService.getColourCodeByModel(modelGroup, modelDescription).subscribe({
      next: (response) => {
        const options: any[] = [
          { value: "", display: "Please select" },
          ...(response.body ?? []).map((colour: any) => ({
            value: colour.ExteriorColour,
            display: `${colour.ExteriorColour} - ${colour.Description}`,
            type: colour.Type,
          })),
        ];
        this.field(fields, "exteriorColourCode").options = options;
        const cleanCode = selectedCode?.split(" - ")[0].trim();
        const selected = options.find((option) => option.value === cleanCode);
        if (selected) {
          this.field(fields, "exteriorColourCode").value = selected.value;
          this.field(fields, "type").value = selected.type ?? "";
          this.loadInteriorColours(fields, modelDescription, selected.value, this.valueOf(fields, "interiorColourCode"));
        }
      },
      error: () => this.notifier.notify("error", "Unable to load exterior colour codes."),
    });
  }

  private loadInteriorColours(fields: FormField[], modelDescription: string, exteriorCode: string, selectedCode?: string): void {
    this.shipmentMasterService.getIntColourCodeList(modelDescription, exteriorCode).subscribe({
      next: (response) => {
        const options: any[] = [
          { value: "", display: "Please select" },
          ...(response.body ?? []).map((colour: any) => ({
            value: colour.Value,
            display: `${colour.Value} - ${colour.Name}`,
          })),
        ];
        this.field(fields, "interiorColourCode").options = options;
        const cleanCode = selectedCode?.split(" - ")[0].trim();
        const selected = options.find((option) => option.value === cleanCode);
        if (selected) {
          this.field(fields, "interiorColourCode").value = selected.value;
        }
      },
      error: () => this.notifier.notify("error", "Unable to load interior colour codes."),
    });
  }

  private setEditFormValues(shipment: ShipmentMaster): void {
    this.setModelOptions(this.shipmentEditFormFields);
    this.field(this.shipmentEditFormFields, "shipmentDate").value = this.toInputDate(shipment.EntryDate);
    this.field(this.shipmentEditFormFields, "arrivalDate").value = this.toInputDate(shipment.ArrivalDate);
    const modelDescriptionField = this.field(this.shipmentEditFormFields, "modelDescription");
    modelDescriptionField.value = this.modelOptions.some((option) => option.value === shipment.ModelName)
      ? shipment.ModelName
      : "";
    this.field(this.shipmentEditFormFields, "modelCode").value = shipment.ModelCode;
    this.field(this.shipmentEditFormFields, "occNumber").value = shipment.OccNo ?? "";
    this.field(this.shipmentEditFormFields, "quantity").value = shipment.Quantity ?? "";
    this.field(this.shipmentEditFormFields, "type").value = shipment.ExtColourType ?? "";
    this.field(this.shipmentEditFormFields, "interiorColourCode").value = shipment.IntColourCode ?? "";
    this.loadExteriorColours(this.shipmentEditFormFields, shipment.ModelGroup, shipment.ModelName, shipment.ExtColourCode ?? undefined);
  }

  private buildShipmentFromFields(fields: FormField[], overrides: Partial<ShipmentMaster>): ShipmentMaster {
    return {
      ShipmentMasterOrderNo: 0,
      ModelGroup: "",
      Classification: null,
      IntColourType: null,
      ReceivedQuantity: 0,
      OrdersRemark: null,
      EtaRemark: null,
      ...overrides,
      ModelName: String(this.valueOf(fields, "modelDescription") ?? ""),
      ModelCode: String(this.valueOf(fields, "modelCode") ?? ""),
      OccNo: String(this.valueOf(fields, "occNumber") ?? ""),
      ExtColourCode: this.optionDisplay(fields, "exteriorColourCode"),
      ExtColourType: String(this.valueOf(fields, "type") ?? ""),
      IntColourCode: this.optionDisplay(fields, "interiorColourCode"),
      Quantity: this.toNumber(this.valueOf(fields, "quantity")),
      EntryDate: this.toBackendDate(this.valueOf(fields, "shipmentDate")),
      ArrivalDate: this.toBackendDate(this.valueOf(fields, "arrivalDate")),
    };
  }

  private refreshPendingEntries(): void {
    this.newShipmentTableEntries = this.pendingShipments.map((shipment) => ({
      shipment,
      entryDate: shipment.EntryDate,
      arrivalDate: shipment.ArrivalDate,
      modelName: shipment.ModelName,
      modelCode: shipment.ModelCode,
      occNumber: shipment.OccNo,
      extColourCode: shipment.ExtColourCode,
      extColourType: shipment.ExtColourType,
      intColourCode: shipment.IntColourCode,
      quantity: shipment.Quantity,
    }));
  }

  private toTableEntry(shipment: ShipmentMaster, includeCheck: boolean): any {
    return {
      no: shipment.ShipmentMasterOrderNo,
      checked: includeCheck ? false : undefined,
      modelName: shipment.ModelName,
      modelCode: shipment.ModelCode,
      entryDate: shipment.EntryDate,
      arrivalDate: shipment.ArrivalDate,
      extColourCode: shipment.ExtColourCode,
      type: shipment.ExtColourType,
      occNumber: shipment.OccNo,
      intColourCode: shipment.IntColourCode,
      quantity: shipment.Quantity,
      receivedQuantity: shipment.ReceivedQuantity,
      etaRemark: shipment.EtaRemark ?? "",
    };
  }

  private selectedHistoryOrderNumbers(): number[] {
    return this.shipmentHistoryTableEntries.filter((entry) => entry.checked).map((entry) => entry.no);
  }

  private createShipmentFields(isEditForm = false): FormField[] {
    return [
      { type: "date", id: "shipmentDate", label: "Shipment Date of Entry (dd/mm/yyyy)", value: "", required: true },
      { type: "date", id: "arrivalDate", label: "Estimated Date of Arrival (dd/mm/yyyy)", value: "", required: true },
      { type: "select", id: "modelDescription", label: "Model Description", value: "", required: !isEditForm, options: this.modelOptions },
      { type: "text", id: "modelCode", label: "Model Code", value: "", required: true },
      { type: "text", id: "occNumber", label: "OCC Number", value: "", maxLength: 150 },
      { type: "select", id: "exteriorColourCode", label: "Exterior Colour Code", value: "", required: true, options: [{ value: "", display: "Please select" }] },
      { type: "text", id: "type", label: "Type", value: "", maxLength: 150 },
      { type: "select", id: "interiorColourCode", label: "Interior Colour Code", value: "", required: true, options: [{ value: "", display: "Please select" }] },
      { type: "number", id: "quantity", label: "Quantity", value: "", required: true, min: 0, step: 1 },
    ];
  }

  private shipmentEntryField(id: string): FormField {
    return { type: "select", id, label: "Shipment Entry", value: "", options: [{ value: "", display: "Please select" }] };
  }

  private shipmentTableDataHeaders(): any[] {
    return [
      { title: "MODEL", type: "data", id: "modelName" },
      { title: "MODEL CODE", type: "data", id: "modelCode" },
      { title: "ENTRY DATE", type: "data", id: "entryDate" },
      { title: "ARRIVAL DATE", type: "data", id: "arrivalDate" },
      { title: "COLOUR CODE", type: "data", id: "extColourCode" },
      { title: "TYPE", type: "data", id: "type" },
      { title: "OCC NUMBER", type: "data", id: "occNumber" },
      { title: "INTERIOR CODE", type: "data", id: "intColourCode" },
      { title: "QUANTITY", type: "data", id: "quantity" },
      { title: "RECEIVED QTY", type: "data", id: "receivedQuantity" },
    ];
  }

  private setModelOptions(fields: FormField[]): void {
    const field = fields.find((item) => item.id === "modelDescription");
    if (field) {
      field.options = this.modelOptions;
    }
  }

  private field(fields: FormField[], id: string): FormField {
    return fields.find((item) => item.id === id)!;
  }

  private valueOf(fields: FormField[], id: string): any {
    return this.field(fields, id).value;
  }

  private optionOf(fields: FormField[], id: string): any {
    return this.field(fields, id).options?.find((option) => option.value === this.valueOf(fields, id));
  }

  private optionDisplay(fields: FormField[], id: string): string {
    return this.optionOf(fields, id)?.display ?? String(this.valueOf(fields, id) ?? "");
  }

  private toNumber(value: any): number | null {
    if (value === "" || value === null || value === undefined) {
      return null;
    }
    const numberValue = Number(value);
    return Number.isFinite(numberValue) ? numberValue : null;
  }

  private toBackendDate(value: any): string {
    const date = String(value ?? "");
    if (/^\d{2}\/\d{2}\/\d{4}$/.test(date)) {
      return date;
    }
    const match = date.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    return match ? `${match[3]}/${match[2]}/${match[1]}` : "";
  }

  private toInputDate(value: string): string {
    const match = String(value ?? "").match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
    return match ? `${match[3]}-${match[2]}-${match[1]}` : value ?? "";
  }

  private isDateAfter(entryDate: string, arrivalDate: string): boolean {
    const [entryDay, entryMonth, entryYear] = entryDate.split("/").map(Number);
    const [arrivalDay, arrivalMonth, arrivalYear] = arrivalDate.split("/").map(Number);
    return new Date(arrivalYear, arrivalMonth - 1, arrivalDay) < new Date(entryYear, entryMonth - 1, entryDay);
  }

  private isSuccessful(response: { code?: number; statusCode: number }): boolean {
    return response.statusCode >= 200 && response.statusCode < 300 && (response.code === undefined || response.code === 0);
  }
}
